import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sendContactMessage } from '../api/client';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import {
  CALENDAR_URL,
  CONTACT_ADDRESS_LINE,
  CONTACT_EMAIL,
  CONTACT_HOURS_CLOSED,
  CONTACT_HOURS_OPEN,
  CONTACT_MAPS_URL,
  formatPhone,
  phoneTelHref,
  PUBLIC_PHONE,
  SERVICE_OFFERS,
} from '../content/services';
import { readCampaignAttribution } from '../lib/campaign';
import { COOKIE_CONSENT_CHANGED_EVENT, hasConsentChoice } from '../lib/cookie-consent';
import { trackConversion } from '../lib/analytics';
import { navigate } from '../router';
import { alertStyles, boxSizingStyles, fullBleedStyles } from '../styles/lit-shared';

const JSON_LD_ID = 'services-jsonld';

const PROCESS = [
  {
    label: '01',
    title: 'La llamada de hoja de ruta',
    body: 'Entendemos el problema, para quién es y qué entra en la primera versión. Sin código y sin suposiciones.',
  },
  {
    label: '02',
    title: 'El brief acordado',
    body: 'Dejo por escrito funcionalidades, tecnología y criterios de éxito. Si no lo firmas, no empiezo.',
  },
  {
    label: '03',
    title: 'Construcción con checkpoints',
    body: 'Sprints cortos con entregas que puedes ver. Al cerrar, tienes algo que se puede lanzar.',
  },
] as const;

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

function servicesJsonLd(): Record<string, unknown> {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': 'https://alejandrosn.com/servicios#service',
    name: 'Desarrollo web, tiendas y automatización',
    url: 'https://alejandrosn.com/servicios',
    description:
      'Webs, tiendas online, automatizaciones, paneles e integraciones para negocios. De la idea a producción.',
    inLanguage: 'es-ES',
    email: CONTACT_EMAIL,
    areaServed: 'ES',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Calle Felipe Sanclemente 8, 4B',
      postalCode: '50001',
      addressLocality: 'Zaragoza',
      addressCountry: 'ES',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
    provider: {
      '@type': 'Person',
      '@id': 'https://alejandrosn.com/#person',
      name: 'Alejandro Serrano Notivoli',
      url: 'https://alejandrosn.com/',
      email: CONTACT_EMAIL,
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Qué puedo construir',
      itemListElement: SERVICE_OFFERS.map((offer, index) => ({
        '@type': 'Offer',
        position: index + 1,
        itemOffered: {
          '@type': 'Service',
          name: offer.name,
          description: offer.example,
        },
      })),
    },
  };

  if (PUBLIC_PHONE) data.telephone = phoneTelHref(PUBLIC_PHONE).replace('tel:', '');
  return data;
}

@customElement('services-page')
export class ServicesPage extends LitElement {
  @state() private name = '';
  @state() private email = '';
  @state() private phone = '';
  @state() private intent = '';
  @state() private message = '';
  @state() private website = '';
  @state() private status: FormStatus = 'idle';
  @state() private errorMessage = '';
  @state() private cookieOpen = !hasConsentChoice();

  private _revealObserver: IntersectionObserver | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._injectJsonLd();
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, this._onConsent);
  }

  firstUpdated() {
    this._setupReveal();
  }

  disconnectedCallback() {
    this._revealObserver?.disconnect();
    this._revealObserver = null;
    window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, this._onConsent);
    document.getElementById(JSON_LD_ID)?.remove();
    super.disconnectedCallback();
  }

  private _onConsent = () => {
    this.cookieOpen = !hasConsentChoice();
  };

  private _injectJsonLd() {
    if (document.getElementById(JSON_LD_ID)) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    el.textContent = JSON.stringify(servicesJsonLd());
    document.head.appendChild(el);
  }

  private _setupReveal() {
    const root = this.shadowRoot;
    if (!root) return;

    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this._revealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    els.forEach((el) => this._revealObserver!.observe(el));
  }

  private goToPrivacy(e: Event) {
    e.preventDefault();
    void navigate('/politica-privacidad');
  }

  private handleInput(e: Event) {
    const el = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const { name, value } = el;
    if (name === 'name') this.name = value;
    else if (name === 'email') this.email = value;
    else if (name === 'phone') this.phone = value;
    else if (name === 'intent') this.intent = value;
    else if (name === 'message') this.message = value;
    else if (name === 'website') this.website = value;
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    if (this.status === 'submitting') return;

    if (!this.intent.trim()) {
      this.status = 'error';
      this.errorMessage = 'Elige qué necesitas.';
      return;
    }

    this.status = 'submitting';
    this.errorMessage = '';

    try {
      await sendContactMessage({
        name: this.name.trim(),
        email: this.email.trim(),
        message: this.message.trim(),
        website: this.website,
        phone: this.phone.trim() || undefined,
        intent: this.intent.trim() || undefined,
        ...readCampaignAttribution(),
      });
      const honeypot = this.website;
      this.status = 'success';
      this.name = '';
      this.email = '';
      this.phone = '';
      this.intent = '';
      this.message = '';
      this.website = '';
      if (!honeypot) trackConversion('form');
    } catch (err) {
      this.status = 'error';
      this.errorMessage =
        err instanceof ApiClientError
          ? err.message
          : 'No se pudo enviar el mensaje. Inténtalo más tarde.';
    }
  }

  private renderActions(label: string) {
    const phoneLabel = PUBLIC_PHONE ? formatPhone(PUBLIC_PHONE) : '';

    return html`
      <div class="contact-block" aria-label=${label}>
        <address class="contact-card">
          ${PUBLIC_PHONE
            ? html`
                <a
                  class="contact-row"
                  href=${phoneTelHref(PUBLIC_PHONE)}
                  @click=${() => trackConversion('phone')}
                >
                  <span class="contact-k">Teléfono</span>
                  <span class="contact-v">
                    <img src="/whatsapp_icon.svg" alt="" width="18" height="18" />
                    ${phoneLabel}
                  </span>
                </a>
              `
            : ''}
          <a
            class="contact-row"
            href=${`mailto:${CONTACT_EMAIL}`}
            @click=${() => trackConversion('email')}
          >
            <span class="contact-k">Email</span>
            <span class="contact-v">${CONTACT_EMAIL}</span>
          </a>
          <a class="contact-row" href=${CONTACT_MAPS_URL} target="_blank" rel="noopener noreferrer">
            <span class="contact-k">Dirección</span>
            <span class="contact-v">${CONTACT_ADDRESS_LINE}</span>
          </a>
          <div class="contact-row contact-row--hours">
            <span class="contact-k">Horario</span>
            <span class="contact-v">
              <span>${CONTACT_HOURS_OPEN}</span>
              <span class="contact-closed">${CONTACT_HOURS_CLOSED}</span>
            </span>
          </div>
        </address>
        <div class="cta-row">
          <ui-button
            href=${CALENDAR_URL}
            variant="primary"
            @ui-click=${() => trackConversion('calendar')}
          >
            Reservar una llamada
          </ui-button>
        </div>
      </div>
    `;
  }

  static styles = [
    boxSizingStyles,
    fullBleedStyles,
    alertStyles,
    css`
      :host {
        display: block;
        width: 100%;
      }

      .hero {
        margin-top: calc(-1 * var(--space-xl));
        margin-bottom: var(--space-md);
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
        background:
          radial-gradient(ellipse 70% 60% at 20% 40%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 50% 50% at 85% 25%, var(--glow-cyan), transparent 50%),
          var(--color-bg);
      }

      .hero-orb {
        position: absolute;
        border-radius: 50%;
        filter: blur(60px);
        pointer-events: none;
        animation: orb-drift 16s var(--ease-out) infinite alternate;
      }

      .hero-orb--1 {
        top: 5%;
        right: 8%;
        width: min(300px, 42vw);
        height: min(300px, 42vw);
        background: var(--glow-cyan);
      }

      .hero-orb--2 {
        bottom: -15%;
        left: 10%;
        width: min(240px, 38vw);
        height: min(240px, 38vw);
        background: var(--glow-fuchsia);
        animation-delay: -6s;
      }

      @keyframes orb-drift {
        from {
          transform: translate(0, 0) scale(1);
        }
        to {
          transform: translate(16px, -12px) scale(1.06);
        }
      }

      .hero-inner,
      .section-inner,
      .band-inner {
        position: relative;
        z-index: 1;
        width: 100%;
        max-width: var(--max-width);
        margin: 0 auto;
      }

      .section-label {
        display: block;
        margin-bottom: var(--space-md);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--color-accent);
      }

      h1 {
        margin: 0 0 var(--space-md);
        font-size: clamp(2.25rem, 6vw, 3.6rem);
        font-weight: 700;
        letter-spacing: -0.04em;
        line-height: 1.08;
        color: var(--color-text);
      }

      h1 .title-grad {
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .lede {
        margin: 0 0 var(--space-lg);
        max-width: 52ch;
        font-size: 1.0625rem;
        line-height: 1.65;
        color: var(--color-text-muted);
      }

      .hero-scope {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.55rem 1.25rem;
        margin: 0 0 var(--space-xl);
        padding: 0;
        list-style: none;
        max-width: 54rem;
      }

      .hero-scope li {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.1rem;
        min-width: 0;
      }

      .scope-name {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-accent);
      }

      .scope-ex {
        font-size: 0.875rem;
        line-height: 1.45;
        color: var(--color-text-muted);
      }

      .contact-block {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      .contact-card {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0;
        margin: 0;
        padding: 0;
        font-style: normal;
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        background: var(--color-surface);
        overflow: hidden;
      }

      .contact-row {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        min-width: 0;
        padding: 0.9rem 1rem;
        color: var(--color-text);
        text-decoration: none;
        border-top: 1px solid var(--color-border);
      }

      .contact-row:first-child {
        border-top: none;
      }

      a.contact-row:hover {
        background: rgb(255 255 255 / 4%);
      }

      .contact-k {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--color-accent);
      }

      .contact-v {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        min-width: 0;
        font-weight: 600;
        line-height: 1.4;
        overflow-wrap: anywhere;
      }

      .contact-v img {
        width: 1.125rem;
        height: 1.125rem;
        flex-shrink: 0;
      }

      .contact-row--hours .contact-v {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.15rem;
      }

      .contact-closed {
        color: var(--color-text-muted);
        font-weight: 500;
      }

      .cta-row {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: var(--space-sm);
      }

      .cta-row ui-button {
        width: 100%;
      }

      .section {
        padding: var(--space-2xl) var(--space-lg);
      }

      h2 {
        margin: 0 0 var(--space-md);
        font-size: clamp(1.75rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1.15;
        color: var(--color-text);
      }

      .section-lead {
        margin: 0 0 var(--space-xl);
        max-width: 52ch;
        color: var(--color-text-muted);
        line-height: 1.6;
      }

      .offer-grid,
      .process {
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--space-md);
        margin: 0;
        padding: 0;
        list-style: none;
      }

      .offer,
      .step {
        min-width: 0;
        padding: var(--space-lg);
        border-radius: var(--radius-lg);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
      }

      .offer h3,
      .step h3 {
        margin: 0 0 var(--space-sm);
        font-size: 1.125rem;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .offer p,
      .step p {
        margin: 0;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .step-label {
        display: block;
        margin-bottom: var(--space-sm);
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        color: var(--color-accent);
      }

      .band {
        padding: var(--space-2xl) var(--space-lg);
        overflow: hidden;
        background:
          radial-gradient(ellipse 60% 70% at 0% 50%, var(--glow-violet), transparent 55%),
          radial-gradient(ellipse 45% 55% at 100% 80%, var(--glow-cyan), transparent 50%),
          var(--color-bg);
      }

      .band--cookie {
        padding-bottom: calc(var(--space-2xl) + 12rem);
      }

      .band-inner {
        max-width: 40rem;
      }

      .form-intro {
        margin-bottom: var(--space-xl);
      }

      .form-intro p {
        margin: 0 0 var(--space-lg);
        color: var(--color-text-muted);
        line-height: 1.6;
      }

      form {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-xs);
      }

      label {
        font-family: var(--font-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-muted);
      }

      input,
      textarea,
      select {
        width: 100%;
        min-height: 2.75rem;
        padding: 0.875rem 1rem;
        border-radius: var(--radius-sm);
        border: 1px solid var(--color-border-strong);
        background: rgb(255 255 255 / 3%);
        color: var(--color-text);
        color-scheme: dark;
        font-family: var(--font-sans);
        font-size: 1rem;
        line-height: 1.5;
        outline: none;
      }

      textarea {
        min-height: 10rem;
        resize: vertical;
      }

      input:focus,
      textarea:focus,
      select:focus {
        border-color: var(--color-accent);
        box-shadow: 0 0 0 2px rgb(34 211 238 / 18%);
      }

      .legal-note {
        margin: 0;
        font-size: 0.8125rem;
        line-height: 1.5;
        color: var(--color-text-subtle);
      }

      .legal-note a {
        color: var(--color-primary-light);
      }

      .hp {
        position: absolute;
        left: -9999px;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
        pointer-events: none;
      }

      .submit {
        margin-top: var(--space-sm);
      }

      .success-block .section-title {
        margin: 0 0 var(--space-md);
        font-size: clamp(1.5rem, 3vw, 2rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        color: var(--color-text);
      }

      .reveal {
        opacity: 0;
        transform: translateY(18px);
        transition:
          opacity 700ms var(--ease-out),
          transform 700ms var(--ease-out);
      }

      .reveal.is-visible {
        opacity: 1;
        transform: translateY(0);
      }

      @media (min-width: 640px) {
        .hero-scope,
        .offer-grid {
          grid-template-columns: 1fr 1fr;
        }

        .cta-row {
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;
        }

        .cta-row ui-button {
          width: auto;
        }
      }

      @media (min-width: 700px) {
        .contact-card {
          grid-template-columns: 0.8fr 1.1fr 1.3fr;
        }

        .contact-row {
          border-top: none;
          border-left: 1px solid var(--color-border);
        }

        .contact-row:first-child {
          border-left: none;
        }

        .contact-row--hours {
          grid-column: 1 / -1;
          border-left: none;
          border-top: 1px solid var(--color-border);
        }

        .contact-row--hours .contact-v {
          flex-direction: row;
          flex-wrap: wrap;
          gap: 0.2rem 1.25rem;
        }

        .process {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      @media (min-width: 860px) {
        .offer-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      @media (max-width: 639px) {
        .hero {
          margin-top: calc(-1 * var(--space-lg));
          padding: var(--space-2xl) var(--space-md);
        }

        .section,
        .band {
          padding-inline: var(--space-md);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero-orb {
          animation: none;
        }

        .reveal {
          opacity: 1;
          transform: none;
          transition: none;
        }
      }
    `,
  ];

  render() {
    const submitting = this.status === 'submitting';

    return html`
      <section class="hero full-bleed" aria-label="Servicios">
        <span class="hero-orb hero-orb--1" aria-hidden="true"></span>
        <span class="hero-orb hero-orb--2" aria-hidden="true"></span>
        <div class="hero-inner">
          <span class="section-label">Servicios</span>
          <h1>De la idea a<br /><span class="title-grad">producción</span></h1>
          <p class="lede">
            Soy Alejandro Serrano. Construyo la web, la tienda, la automatización o la
            integración que tu negocio necesita. El código empieza cuando el alcance está claro.
          </p>
          <ul class="hero-scope" aria-label="Qué puedo construir">
            ${SERVICE_OFFERS.map(
              (item) => html`
                <li>
                  <span class="scope-name">${item.name}</span>
                  <span class="scope-ex">${item.example}</span>
                </li>
              `,
            )}
          </ul>
          ${this.renderActions('Contactar')}
        </div>
      </section>

      <section class="section" aria-labelledby="offers-title">
        <div class="section-inner">
          <span class="section-label reveal">Alcance</span>
          <h2 id="offers-title" class="reveal">Qué puedo hacer por ti</h2>
          <p class="section-lead reveal">
            Si el tuyo encaja en uno, o en varios, lo vemos en la llamada.
          </p>
          <div class="offer-grid">
            ${SERVICE_OFFERS.map(
              (offer) => html`
                <article class="offer reveal">
                  <h3>${offer.name}</h3>
                  <p>${offer.example}</p>
                </article>
              `,
            )}
          </div>
        </div>
      </section>

      <section class="section" aria-labelledby="process-title">
        <div class="section-inner">
          <span class="section-label reveal">Proceso</span>
          <h2 id="process-title" class="reveal">Cómo empieza</h2>
          <p class="section-lead reveal">
            Tres pasos. Nada de reuniones infinitas ni entregas a ciegas.
          </p>
          <ol class="process">
            ${PROCESS.map(
              (step) => html`
                <li class="step reveal">
                  <span class="step-label">${step.label}</span>
                  <h3>${step.title}</h3>
                  <p>${step.body}</p>
                </li>
              `,
            )}
          </ol>
        </div>
      </section>

      <section
        class="band full-bleed ${this.cookieOpen ? 'band--cookie' : ''}"
        aria-labelledby="form-title"
      >
        <div class="band-inner reveal">
          ${this.status === 'success'
            ? html`
                <div class="success-block" role="status">
                  <span class="section-label">Enviado</span>
                  <h2 id="form-title" class="section-title">Lo he recibido</h2>
                  <div class="alert alert--success">
                    Te contesto al email que me has dejado. Si encaja, el siguiente paso es la
                    llamada de hoja de ruta.
                  </div>
                </div>
              `
            : html`
                <div class="form-intro">
                  <span class="section-label">Contacto</span>
                  <h2 id="form-title">Cuéntame el proyecto</h2>
                  <p>
                    Nombre, email y qué necesitas. Si prefieres no escribir, llama, reserva o
                    manda un correo.
                  </p>
                  ${this.renderActions('Otras formas de contacto')}
                </div>

                <form @submit=${this.handleSubmit} novalidate>
                  <div class="field">
                    <label for="service-name">Nombre</label>
                    <input
                      id="service-name"
                      type="text"
                      name="name"
                      autocomplete="name"
                      maxlength="100"
                      required
                      placeholder="Cómo te llamas"
                      .value=${this.name}
                      @input=${this.handleInput}
                    />
                  </div>

                  <div class="field">
                    <label for="service-email">Email</label>
                    <input
                      id="service-email"
                      type="email"
                      name="email"
                      autocomplete="email"
                      maxlength="254"
                      required
                      placeholder="tu@email.com"
                      .value=${this.email}
                      @input=${this.handleInput}
                    />
                  </div>

                  <div class="field">
                    <label for="service-phone">Teléfono (opcional)</label>
                    <input
                      id="service-phone"
                      type="tel"
                      name="phone"
                      autocomplete="tel"
                      maxlength="40"
                      placeholder="+34 600 000 000"
                      .value=${this.phone}
                      @input=${this.handleInput}
                    />
                  </div>

                  <div class="field">
                    <label for="service-intent">Qué necesitas</label>
                    <select
                      id="service-intent"
                      name="intent"
                      required
                      .value=${this.intent}
                      @change=${this.handleInput}
                    >
                      <option value="" disabled>Elige una opción</option>
                      ${SERVICE_OFFERS.map(
                        (offer) => html`<option value=${offer.name}>${offer.name}</option>`,
                      )}
                    </select>
                  </div>

                  <div class="field">
                    <label for="service-message">Mensaje</label>
                    <textarea
                      id="service-message"
                      name="message"
                      maxlength="5000"
                      required
                      placeholder="Qué quieres construir, para quién y en qué punto estás."
                      .value=${this.message}
                      @input=${this.handleInput}
                    ></textarea>
                  </div>

                  <div class="hp" aria-hidden="true">
                    <label for="service-website">Website</label>
                    <input
                      id="service-website"
                      type="text"
                      name="website"
                      tabindex="-1"
                      autocomplete="off"
                      .value=${this.website}
                      @input=${this.handleInput}
                    />
                  </div>

                  <p class="legal-note">
                    Uso estos datos solo para responderte.
                    <a href="/politica-privacidad" @click=${this.goToPrivacy}
                      >Política de privacidad</a
                    >.
                  </p>

                  <div class="submit">
                    <ui-button type="submit" variant="primary" fullwidth ?disabled=${submitting}>
                      ${submitting ? 'Enviando…' : 'Enviar mensaje'}
                    </ui-button>
                  </div>

                  ${this.status === 'error'
                    ? html`
                        <div class="alert alert--error" role="alert">${this.errorMessage}</div>
                      `
                    : ''}
                </form>
              `}
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'services-page': ServicesPage;
  }
}
